import React, { type ComponentProps, ReactNode, useMemo } from 'react';

import { CSSProp } from 'styled-components';

import { useDropdown } from '../hooks';
import { Control } from '../layout';
import Placeholder from '../layout/Control/Placeholder';
import type {
  GenericItem,
  RenderGroupHeaderProps,
  UseDropdownProps,
  VirtualProps,
} from '../types';
import { ItemToString, RenderMenuItem } from '../types';
import DropdownMenu from './Menu/DropdownMenu';
import { filterUndefined } from '../helpers/filterUndefined';

type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof Control<TItem>
>;

type MenuProps<TItem extends GenericItem> = ComponentProps<
  typeof DropdownMenu<TItem>
>;

export type DropdownTemplateProps<TItem extends GenericItem> =
  UseDropdownProps<TItem> & {
    style?: CSSProp;
    /**
     * Disable the entire dropdown
     */
    disabled?: boolean;
    /**
     * Enable error state
     */
    error?: boolean;
    /**
     * Enable loading state
     */
    isLoading?: boolean;
    /**
     * Placeholder text to display
     */
    placeholder?: string;
    /**
     * Function to render a custom dropdown control
     * @param args Props to be provided to the control
     * @returns A dropdown control element
     */
    renderControl?: (args: ControlProps<TItem>) => React.ReactNode;
    /**
     * Function to render a custom dropdown menu
     * @param args Props to be provided to the menu
     * @param virtualProps Props used to virtualize the menu - see stories for examples
     * @returns A dropdown menu element
     */
    renderMenu?: (
      args: MenuProps<TItem>,
      virtualProps: VirtualProps,
    ) => React.ReactNode;
    /**
     * Function to render a custom input element
     */
    renderInput?: () => ReactNode;
    /**
     * Function to render a custom placeholder element
     * @param props Props to be provided to the placeholder
     * @returns A placeholder element
     */
    renderPlaceholder?: (
      props?: ComponentProps<typeof Placeholder>,
    ) => ReactNode;
    /**
     * Function to render a custom selected item
     * @param selectedItem The selected item
     * @param itemToString Function to convert an item to a string
     * @param isOpen Whether the dropdown is open
     */
    renderItem?: ({
      selectedItem,
      itemToString,
      isOpen,
    }: {
      selectedItem?: TItem | null;
      itemToString: ItemToString<TItem>;
      isOpen: boolean;
    }) => ReactNode;
    /**
     * Whether to use less spacing for a more compact UI
     */
    highDensity?: boolean;
    /**
     * Whether to highlight the toggle button on focus
     */
    highlightButtonOnFocus?: boolean;
    /**
     * Function to render a custom menu item
     */
    renderMenuItem?: RenderMenuItem<TItem>;
    /**
     * Function to render a custom group header menu item
     */
    renderGroupHeader?: (props: RenderGroupHeaderProps) => ReactNode;
  };

const defaultRenderControl = <TItem extends GenericItem>(
  args: ControlProps<TItem>,
) => <Control {...args} />;

/**
 * A single select, non-searchable dropdown component.
 *
 * This component covers the majority of use cases, but advanced use cases may require using the `useDropdown` hook directly.
 */
const Dropdown = <TItem extends GenericItem>({
  style,
  disabled,
  error,
  isLoading,
  placeholder,
  renderControl = defaultRenderControl,
  renderMenu = DropdownMenu.render,
  renderItem,
  renderInput,
  renderPlaceholder,
  renderMenuItem,
  renderGroupHeader,
  highDensity,
  highlightButtonOnFocus,
  ...args
}: DropdownTemplateProps<TItem>) => {
  const { controlProps, menuProps, virtualProps } = useDropdown(args);

  // Filter out undefined props to ensure they are not passed to the control
  // as explicitly undefined and overwrite other props
  const filteredControlProps = useMemo(
    () =>
      filterUndefined({
        disabled,
        error,
        isLoading,
        placeholder,
        renderItem,
        renderInput,
        renderPlaceholder,
        highDensity,
        highlightButtonOnFocus,
      }),
    [
      disabled,
      error,
      isLoading,
      placeholder,
      renderItem,
      renderInput,
      renderPlaceholder,
      highDensity,
      highlightButtonOnFocus,
    ],
  );
  const filteredMenuProps = useMemo(
    () => filterUndefined({ renderMenuItem, renderGroupHeader }),
    [renderMenuItem, renderGroupHeader],
  );

  return (
    <div css={[{ width: 300 }, style]}>
      {renderControl({
        ...filteredControlProps,
        ...controlProps,
      })}
      {renderMenu({ ...menuProps, ...filteredMenuProps }, virtualProps)}
    </div>
  );
};

export default Dropdown;
